import * as PortOne from '@portone/browser-sdk/v2';

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

export class PortOneCheckoutError extends Error {
    constructor(code, message, failureReason = 'provider_error') {
        super(message);
        this.name = 'PortOneCheckoutError';
        this.code = code;
        this.failureReason = failureReason;
    }
}

export function resolvePortOneConfig(env = import.meta.env) {
    const mode = String(env.VITE_PORTONE_MODE || 'test').trim().toLowerCase();
    const storeId = String(env.VITE_PORTONE_STORE_ID || '').trim();
    const channelKey = String(
        mode === 'live'
            ? env.VITE_PORTONE_CHANNEL_KEY_LIVE || ''
            : env.VITE_PORTONE_CHANNEL_KEY_TEST || '',
    ).trim();

    if (!['test', 'live'].includes(mode)) {
        throw new PortOneCheckoutError(
            'portone_invalid_mode',
            'PortOne payment mode is invalid.',
            'configuration_error',
        );
    }
    if (!storeId || !channelKey) {
        throw new PortOneCheckoutError(
            'portone_not_configured',
            'PortOne payment is not configured.',
            'configuration_error',
        );
    }
    return { mode, storeId, channelKey };
}

async function getPaymentApiClient() {
    const { apiFetchPayment } = await import('../api/client.js');
    return apiFetchPayment;
}

export async function initPortOnePayment(productId, resultId) {
    const apiFetchPayment = await getPaymentApiClient();
    return apiFetchPayment('/payments/portone/init', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, result_id: resultId || undefined }),
    });
}

export async function confirmPortOnePayment(paymentId) {
    const apiFetchPayment = await getPaymentApiClient();
    return apiFetchPayment('/payments/portone/confirm', {
        method: 'POST',
        body: JSON.stringify({ payment_id: paymentId }),
    });
}

export function buildPortOnePaymentRequest(order, config) {
    return {
        storeId: config.storeId,
        channelKey: config.channelKey,
        paymentId: order.payment_id,
        orderName: order.order_name,
        totalAmount: order.total_amount,
        currency: 'KRW',
        payMethod: 'CARD',
    };
}

function withTimeout(promise, timeoutMs) {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
            reject(new PortOneCheckoutError(
                'portone_timeout',
                'Payment window timed out.',
                'timeout',
            ));
        }, timeoutMs);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

function responseError(response) {
    const code = String(response?.code || 'portone_payment_failed');
    const isCancelled = /cancel|close/i.test(code) || /취소|닫/i.test(String(response?.message || ''));
    return new PortOneCheckoutError(
        code,
        isCancelled ? 'Payment was cancelled.' : 'Payment could not be completed.',
        isCancelled ? 'user_cancelled' : 'provider_error',
    );
}

export async function executePortOnePayment({
    productId,
    resultId,
    env = import.meta.env,
    requestPayment = PortOne.requestPayment,
    initPayment = initPortOnePayment,
    confirmPayment = confirmPortOnePayment,
    onOrderCreated,
    timeoutMs = DEFAULT_TIMEOUT_MS,
}) {
    const config = resolvePortOneConfig(env);
    const order = await initPayment(productId, resultId);
    if (onOrderCreated) onOrderCreated(order);

    const response = await withTimeout(
        Promise.resolve(requestPayment(buildPortOnePaymentRequest(order, config))),
        timeoutMs,
    );
    if (!response) {
        throw new PortOneCheckoutError(
            'portone_window_closed',
            'Payment was cancelled.',
            'user_cancelled',
        );
    }
    if (response.code) throw responseError(response);
    if (response.paymentId !== order.payment_id) {
        throw new PortOneCheckoutError(
            'portone_payment_id_mismatch',
            'Payment verification failed.',
            'verification_failed',
        );
    }

    const confirmation = await confirmPayment(order.payment_id);
    if (confirmation?.status !== 'fulfilled') {
        throw new PortOneCheckoutError(
            'portone_not_fulfilled',
            'Payment verification failed.',
            'verification_failed',
        );
    }
    return { order, response, confirmation };
}
